import {AxiosError} from "axios";
import {AlertCircle} from "lucide-react";
import {useEffect, useRef} from "react";
import {useForm, useWatch, type UseFormReturn} from "react-hook-form";
import {useTranslation} from "react-i18next";
import {toast} from "sonner";

import {Alert, AlertTitle} from "@/components/ui/alert";
import {Button} from "@/components/ui/button";
import {ComboboxGeneric} from "@/components/ui/combobox-generic";
import {FakeProgress} from "@/components/ui/fake-progress";
import {Form, FormControl, FormField, FormItem, FormLabel} from "@/components/ui/form";
import FormError from "@/components/ui/form-error";
import ImagePicker from "@/components/ui/image-picker";
import {Input} from "@/components/ui/input";
import {Item, ItemContent, ItemDescription, ItemTitle} from "@/components/ui/item";
import {Label} from "@/components/ui/label";
import {Sheet, SheetContent, SheetFooter} from "@/components/ui/sheet";
import {Switch} from "@/components/ui/switch";
import {Textarea} from "@/components/ui/textarea";
import {TOAST_CONFIG} from "@/lib/constansts";
import useAmenityAccessLogCreate from "@/src/modules/amenities/hooks/use-amenity-access-log-create";
import useAmenityAccessLog from "@/src/modules/amenities/hooks/use-amenity-access-log";
import useAmenityAccessLogUpdate from "@/src/modules/amenities/hooks/use-amenity-access-log-update";
import type {AmenityConfig} from "@/src/modules/amenities/lib/amenity-config";
import {
  amenityAccessLogHasPersistableContent,
  deserializeAmenityAccessLogFormDraftValues,
  getAmenityAccessLogFormDraftKey,
  getAmenityAccessLogSheetId,
  serializeAmenityAccessLogFormDraftValues,
} from "@/src/modules/amenities/lib/amenity-access-log-form-draft";
import {getAmenityGuestLimit} from "@/src/modules/amenities/lib/guest-limit";
import {
  getAmenityAccessLogHelp,
  type AmenityAccessLogHelp,
} from "@/src/modules/amenities/lib/amenity-access-log-help";
import {resolveGpsForSubmit} from "@/src/modules/core/lib/resolve-gps-coordinates-string";
import {
  tAmenityAccessLogDetailsCharCount,
  tAmenityAccessLogFacilityLabel,
  tAmenityAccessLogFacilityRequired,
  tAmenityAccessLogGuestLimitAlert,
  tAmenityAccessLogImagePickerLabels,
  tAmenityAccessLogSheetTitle,
  tAmenityAccessLogSubmitLabel,
} from "@/src/modules/amenities/lib/amenity-access-log-sheet-i18n";
import {CoordinatesRequiredGate, Loader} from "@/src/modules/core/components";
import {useFormDraft, type ClearFormDraft} from "@/src/modules/core/hooks/use-form-draft";
import {useRestoreFormDraft} from "@/src/modules/core/hooks/use-restore-form-draft";
import {createFormInvalidHandler} from "@/src/modules/core/lib/glitchtip/product-telemetry";
import {
  FORM_SHEET_FOOTER_CLASS,
  FORM_SHEET_ROOT_FORM_CLASS,
} from "@/src/modules/core/lib/form-sheet-layout";
import {useCoordinatesGate} from "@/src/modules/core/hooks/use-coordinates-gate";
import {useStoredMeProperties} from "@/src/modules/core/store/me";
import {useStoredUserProperty} from "@/src/modules/core/store/user-property";
import {useCreateFiles} from "@/src/modules/files/hooks";
import {HouseholdCombobox} from "@/src/modules/households/components/combobox";
import type {Household} from "@/src/modules/households/types";
import {HouseholdAmenityRestrictionBanner} from "@/src/modules/households/components/household-amenity-restriction-banner";
import {
  isAmenityKeyRestricted,
  type AmenityRestrictionCheckSource,
} from "@/src/modules/households/lib/household-amenity-restriction";
import {
  getHouseholdAmenityRestrictionErrorMessage,
  isHouseholdAmenityRestrictionError,
} from "@/src/modules/households/lib/is-household-amenity-restriction-error";
import type {PropertyAmenity} from "@/src/modules/amenities/types";
import {PropertyAmenitiesCombobox} from "@/src/modules/properties/components/property-amenities-combobox";
import {FieldHelpLabel} from "@/src/modules/help/components/field-help";
import {SheetFormScrollBody} from "@/src/modules/help/components/sheet-form-scroll-body";
import {SheetHelpHeader} from "@/src/modules/help/components/sheet-help-header";
import {useCancelHelpTourOnSheetClose} from "@/src/modules/help/hooks/use-cancel-help-tour-on-sheet-close";

function getAccessLogHousehold(accessLog: any) {
  if (!accessLog) {
    return null;
  }
  const household = accessLog.house_hold ?? accessLog.household;
  if (!household) {
    return null;
  }
  const houseHoldId = household.house_hold_id ?? household.id;
  if (houseHoldId == null || houseHoldId === 0) {
    return null;
  }
  return {
    house_hold_id: houseHoldId,
    name: household.name ?? "",
  };
}

const getDefaultValues = (accessLog: any, userProperty: any) => {
  const amenity = accessLog?.property_amenity;
  return {
    property_amenity: amenity
      ? {property_amenity_id: amenity.property_amenity_id, name: amenity.name}
      : null,
    house_hold: getAccessLogHousehold(accessLog),
    property: userProperty,
    name: accessLog?.name ?? "",
    number_of_guests: accessLog?.number_of_guests ?? "",
    key_fob_number: accessLog?.key_fob_number ?? "",
    details: accessLog?.details ?? "",
    send_notification: false,
    files: accessLog?.files ?? [],
  };
};

export function AmenityAccessLogSheet({
  config,
  isVisible,
  amenityAccessLogId,
  onClose,
  draftRestoreValues,
  onDraftRestored,
}: {
  config: AmenityConfig;
  isVisible: boolean;
  amenityAccessLogId: number | null;
  onClose: () => void;
  draftRestoreValues?: Record<string, unknown> | null;
  onDraftRestored?: () => void;
}) {
  const formRef = useRef<UseFormReturn<any> | null>(null);
  const draftHelpersRef = useRef<{clearDraft: ClearFormDraft} | null>(null);
  const {t} = useTranslation("amenity_access_log");
  const property = useStoredUserProperty();
  const guestLimit = getAmenityGuestLimit(property, config);
  const help = getAmenityAccessLogHelp(config);

  const {accessLog, isPending} = useAmenityAccessLog(config, amenityAccessLogId);

  const {mutateAsync: mutateCreateFiles} = useCreateFiles();
  const {mutateAsync: createAccessLog} = useAmenityAccessLogCreate({config});
  const {mutateAsync: updateAccessLog} = useAmenityAccessLogUpdate({config});

  useCancelHelpTourOnSheetClose(isVisible);

  const handleOpenChange = (open: boolean) => {
    if (open) {
      return;
    }
    draftHelpersRef.current?.clearDraft();
    onClose();
  };

  const onSubmit = async (data: any) => {
    let files = data.files.filter((i: any) => !(i instanceof File));
    const toProcess = data.files.filter((i: any) => i instanceof File);

    if (toProcess.length && navigator.onLine) {
      const newFiles = await mutateCreateFiles(toProcess);
      files = [...files, ...newFiles];
    } else if (!navigator.onLine) {
      files = data.files;
    }

    const gps = await resolveGpsForSubmit(
      {
        propertyCoordinates:
          data.property?.gps_coordinates ?? property?.gps_coordinates ?? null,
      },
      {
        captureContext: {
          feature: config.guestRuleFeature,
          operation: amenityAccessLogId
            ? `amenity_access_log_${config.type}_update`
            : `amenity_access_log_${config.type}_create`,
        },
      },
    );
    if (!gps) {
      return;
    }

    const payload = {
      ...data,
      property_amenity_id: data.property_amenity?.property_amenity_id,
      house_hold_id: data.house_hold.house_hold_id,
      property_id: data.property.id,
      send_notification: data.send_notification ? 1 : 0,
      gps_coordinates: gps.gps_coordinates,
      gps_source: gps.gps_source,
      files,
    };
    delete payload.property_amenity;
    delete payload.house_hold;

    if (guestLimit) {
      payload.number_of_allowed_guests = guestLimit;
    }

    if (!navigator.onLine) {
      toast.error(t("sheet.offline_submit"), TOAST_CONFIG);
      return;
    }

    try {
      if (!amenityAccessLogId) {
        await createAccessLog(payload);
      } else {
        payload["amenity_access_log_id"] = amenityAccessLogId;
        await updateAccessLog(payload);
      }
      draftHelpersRef.current?.clearDraft({submitted: true});
      onClose();
    } catch (error) {
      if (isHouseholdAmenityRestrictionError(error)) {
        toast.error(getHouseholdAmenityRestrictionErrorMessage(error), TOAST_CONFIG);
        return;
      }

      const responseData = error instanceof AxiosError ? (error.response?.data as any) : null;
      const hasGpsFieldError =
        responseData?.errors?.gps_coordinates !== undefined ||
        String(responseData?.message ?? "")
          .toLowerCase()
          .includes("gps_coordinates");

      if (error instanceof AxiosError && error.response?.status === 422 && hasGpsFieldError) {
        toast.error(
          t("validation.gps_format"),
          TOAST_CONFIG,
        );
        return;
      }

      const message = responseData?.message ?? t("sheet.submit_error");
      toast.error(String(message), TOAST_CONFIG);
    }
  };

  const handleInit = (helpers: {
    form: UseFormReturn<any>;
    clearDraft: ClearFormDraft;
  }) => {
    formRef.current = helpers.form;
    draftHelpersRef.current = helpers;
  };

  return (
    <Sheet
      open={isVisible}
      askConfirmation={() => {
        if (!formRef.current) return false;
        return Object.keys(formRef.current.formState.dirtyFields).length > 0;
      }}
      onOpenChange={handleOpenChange}
    >
      <SheetContent className="flex min-h-0 flex-col gap-0 overflow-hidden p-0">
        <SheetHelpHeader
          title={tAmenityAccessLogSheetTitle(!!amenityAccessLogId, t)}
          createTourId={help.tours.formCreate}
          createTourLabel={t("sheet.show_me_how")}
          showCreateTour={!amenityAccessLogId}
        />
        {isPending ? (
          <Loader className="h-full min-h-48" />
        ) : (
          <AmenityAccessLogForm
            config={config}
            help={help}
            isVisible={isVisible ?? null}
            isEditing={!!amenityAccessLogId}
            amenityAccessLogId={amenityAccessLogId}
            guestLimit={guestLimit ?? null}
            accessLog={accessLog}
            draftRestoreValues={draftRestoreValues}
            onDraftRestored={onDraftRestored}
            onSubmit={onSubmit}
            onInit={handleInit}
          />
        )}
      </SheetContent>
    </Sheet>
  );
}

function AmenityAccessLogForm({
  config,
  help,
  isVisible,
  isEditing,
  amenityAccessLogId,
  guestLimit,
  accessLog,
  draftRestoreValues,
  onDraftRestored,
  onSubmit,
  onInit,
}: {
  config: AmenityConfig;
  help: AmenityAccessLogHelp;
  isVisible: boolean;
  isEditing: boolean;
  amenityAccessLogId: number | null;
  guestLimit: number | null | undefined;
  accessLog: any;
  draftRestoreValues?: Record<string, unknown> | null;
  onDraftRestored?: () => void;
  onSubmit: (data: any) => Promise<void>;
  onInit: (helpers: {
    form: UseFormReturn<any>;
    clearDraft: ClearFormDraft;
  }) => void;
}) {
  const skipOpenResetRef = useRef(false);
  const {t} = useTranslation("amenity_access_log");
  const userProperty = useStoredUserProperty();
  const userProperties = useStoredMeProperties();

  const properties = userProperties.filter((p: any) => p.is_hidden === 0);
  const hasPropertySelector = properties.length > 1;
  const defaultValues = getDefaultValues(accessLog, userProperty);
  const form = useForm({
    defaultValues,
  });
  const {
    watch,
    setValue,
    getValues,
    control,
    handleSubmit,
    reset,
    formState: {errors, isSubmitting},
  } = form;

  const property = watch("property");
  const prevPropertyIdRef = useRef<number | undefined>(property?.id);
  const houseHold = watch("house_hold");
  const files = useWatch({control, name: "files", defaultValue: []});
  const numberOfGuests = watch("number_of_guests");
  const {submitBlockedByLocation} = useCoordinatesGate(true, {active: isVisible});

  const draftKey = getAmenityAccessLogFormDraftKey(
    config,
    isEditing,
    amenityAccessLogId,
    userProperty?.id,
  );

  const {snapshotDraft, clearDraft} = useFormDraft({
    draftKey,
    moduleLabel: config.labelAccessLogs,
    route: config.routeAccessLogs,
    sheetId: getAmenityAccessLogSheetId(config),
    entityId: amenityAccessLogId ?? undefined,
    contextPropertyId: userProperty?.id,
    enabled: isVisible,
    getValues,
    serializeValues: serializeAmenityAccessLogFormDraftValues,
    shouldPersist: () => amenityAccessLogHasPersistableContent(getValues()),
    watch,
    abandonTelemetry: {
      feature: config.guestRuleFeature,
      operation: isEditing
        ? `amenity_access_log_${config.type}_update`
        : `amenity_access_log_${config.type}_create`,
    },
  });

  useRestoreFormDraft({
    draftRestoreValues,
    onRestore: () => {
      reset({
        ...defaultValues,
        ...deserializeAmenityAccessLogFormDraftValues(draftRestoreValues!),
      });
      skipOpenResetRef.current = true;
      onDraftRestored?.();
    },
  });
  const isHouseholdRestricted =
    !isEditing &&
    isAmenityKeyRestricted(
      houseHold as unknown as AmenityRestrictionCheckSource | null,
      config.type,
    );
  const values = watch();
  const hasAllRequiredFields =
    Boolean(values.house_hold) &&
    Boolean(String(values.name ?? "").trim()) &&
    Boolean(values.property_amenity) &&
    values.number_of_guests !== "" &&
    values.number_of_guests != null &&
    Boolean(String(values.key_fob_number ?? "").trim()) &&
    Boolean(String(values.details ?? "").trim()) &&
    (!hasPropertySelector || Boolean(values.property));
  const isPool = config.type === "pool";
  const facilityLabel = tAmenityAccessLogFacilityLabel(isPool, config.labelFacilitySingular, t);
  const facilityRequiredMessage = tAmenityAccessLogFacilityRequired(isPool, facilityLabel, t);
  const imagePickerLabels = tAmenityAccessLogImagePickerLabels(files?.length ?? 0, 5, t);

  const handleUploadFiles = async (newFiles: any) => {
    setValue("files", newFiles, {shouldDirty: true});
    snapshotDraft();
  };

  const removeFiles = (index: number) => {
    setValue(
      "files",
      getValues("files").filter((_: any, i: number) => i !== index),
      {shouldDirty: true},
    );
    snapshotDraft();
  };

  useEffect(() => {
    onInit?.({form, clearDraft});
  }, [form, clearDraft, onInit]);

  useEffect(() => {
    const propertyId = property?.id;
    if (prevPropertyIdRef.current === propertyId) {
      return;
    }
    prevPropertyIdRef.current = propertyId;
    if (skipOpenResetRef.current) {
      skipOpenResetRef.current = false;
      return;
    }
    setValue("house_hold", null);
  }, [property?.id, setValue]);

  return (
    <Form {...(form as any)}>
      <form
        onSubmit={handleSubmit(
          onSubmit,
          createFormInvalidHandler({
            feature: config.guestRuleFeature,
            operation: isEditing
              ? `amenity_access_log_${config.type}_update`
              : `amenity_access_log_${config.type}_create`,
            sheetId: getAmenityAccessLogSheetId(config),
          }),
        )}
        className={FORM_SHEET_ROOT_FORM_CLASS}
      >
        <SheetFormScrollBody className="flex flex-col gap-4 px-4 pt-2 pb-4">
        <CoordinatesRequiredGate />
        <fieldset
          disabled={submitBlockedByLocation}
          className="m-0 flex min-w-0 flex-col gap-6 border-0 p-0"
        >
        {hasPropertySelector ? (
          <FormField
            control={form.control}
            name="property"
            rules={{required: t("validation.property_required")}}
            render={({field, formState: {errors: fieldErrors}}) => (
              <FormItem>
                <FieldHelpLabel helpKey={help.form.property} showIcon={false}>
                  <FormLabel>{t("form.property")}</FormLabel>
                </FieldHelpLabel>
                <FormControl>
                  <ComboboxGeneric
                    items={properties}
                    {...field}
                    placeholder={t("form.property_placeholder")}
                    preserveCase
                    getItemKey={(item: any) => item.id}
                    getItemLabel={(item: any) => item.name}
                    isItemEqual={(item: any, selected: any) => item.id === selected?.id}
                    renderItem={(option: any) => (
                      <Item size="sm" className="p-0">
                        <ItemContent>
                          <ItemTitle className="font-normal">{option.name}</ItemTitle>
                          <ItemDescription className="text-xs">
                            {option.street} {option.number}, {option.city}, {option.state}{" "}
                            {option.zipcode}
                          </ItemDescription>
                        </ItemContent>
                      </Item>
                    )}
                  />
                </FormControl>
                {fieldErrors.property && <FormError error={fieldErrors.property.message} />}
              </FormItem>
            )}
          />
        ) : null}
        <FormField
          control={control}
          name="house_hold"
          rules={{required: t("validation.household_required")}}
          render={({field}) => (
            <FormItem>
              <FieldHelpLabel helpKey={help.form.household} showIcon={false}>
                <Label htmlFor="house_hold" className="text-foreground">
                  {t("form.household")}
                </Label>
              </FieldHelpLabel>
              <HouseholdCombobox
                {...(field as any)}
                className="min-w-0 w-full"
                filter={{context_property_id: property.id}}
              />
              {errors.house_hold && <FormError error={errors.house_hold.message} />}
            </FormItem>
          )}
        />
        {isHouseholdRestricted ? <HouseholdAmenityRestrictionBanner /> : null}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <FormField
            control={control}
            name="name"
            rules={{required: t("validation.resident_required")}}
            render={({field}) => (
              <FormItem>
                <FieldHelpLabel helpKey={help.form.resident} showIcon={false}>
                  <Label htmlFor="name" className="text-foreground">
                    {t("form.resident")}
                  </Label>
                </FieldHelpLabel>
                <Input {...field} />
                {errors.name && <FormError error={errors.name.message} />}
              </FormItem>
            )}
          />
          <FormField
            control={control}
            name="property_amenity"
            rules={{required: facilityRequiredMessage}}
            render={({field}) => (
              <FormItem>
                <FieldHelpLabel helpKey={help.form.facility} showIcon={false}>
                  <Label htmlFor="property_amenity" className="text-foreground">
                    {facilityLabel}
                  </Label>
                </FieldHelpLabel>
                <PropertyAmenitiesCombobox config={config} {...(field as any)} />
                {errors.property_amenity && <FormError error={errors.property_amenity.message} />}
              </FormItem>
            )}
          />
        </div>

        {guestLimit ? (
          <Alert variant={numberOfGuests > guestLimit ? "warning" : "info"}>
            <AlertCircle className="size-4" />
            <AlertTitle>{tAmenityAccessLogGuestLimitAlert(guestLimit, t)}</AlertTitle>
          </Alert>
        ) : null}

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <FormField
            control={control}
            name="number_of_guests"
            rules={{required: t("validation.guests_required"), min: 0}}
            render={({field}) => (
              <FormItem>
                <FieldHelpLabel helpKey={help.form.guests} showIcon={false}>
                  <Label htmlFor="number_of_guests" className="text-foreground">
                    {t("form.guests")}
                  </Label>
                </FieldHelpLabel>
                <Input type="number" min={0} {...field} />
                {errors.number_of_guests && <FormError error={errors.number_of_guests.message} />}
              </FormItem>
            )}
          />
          <FormField
            control={control}
            name="key_fob_number"
            rules={{required: t("validation.key_fob_required")}}
            render={({field}) => (
              <FormItem>
                <FieldHelpLabel helpKey={help.form.keyFobNumber} showIcon={false}>
                  <Label htmlFor="key_fob_number" className="text-foreground">
                    {t("form.key_fob")}
                  </Label>
                </FieldHelpLabel>
                <Input {...field} />
                {errors.key_fob_number && <FormError error={errors.key_fob_number.message} />}
              </FormItem>
            )}
          />
        </div>

        <FormField
          control={control}
          name="details"
          rules={{required: t("validation.details_required")}}
          render={({field}) => (
            <FormItem>
              <FieldHelpLabel helpKey={help.form.details} showIcon={false}>
                <Label htmlFor="details" className="text-foreground">
                  {t("form.details")}
                </Label>
              </FieldHelpLabel>
              <Textarea maxLength={500} rows={5} className="resize-none" {...field} />
              <p className="ml-auto text-sm text-muted-foreground">
                {tAmenityAccessLogDetailsCharCount(field.value?.length ?? 0, 500, t)}
              </p>
              {errors.details && <FormError error={errors.details.message} />}
            </FormItem>
          )}
        />

        <FormItem>
          <FieldHelpLabel helpKey={help.form.images} showIcon={false}>
            <Label htmlFor="files" className="text-foreground">
              {t("form.images")}
            </Label>
          </FieldHelpLabel>
          <ImagePicker
            images={files ?? []}
            errors={errors}
            immediateUpload
            captureFeature={config.guestRuleFeature}
            captureOperation={
              isEditing
                ? `amenity_access_log_${config.type}_update`
                : `amenity_access_log_${config.type}_create`
            }
            onBeforePick={snapshotDraft}
            onUpload={handleUploadFiles}
            onDelete={removeFiles}
            labels={imagePickerLabels}
          />
          {files?.length > 0 && isSubmitting ? <FakeProgress /> : null}
        </FormItem>

        <FormField
          control={control}
          name="send_notification"
          render={({field}) => (
            <FormItem className="flex flex-row items-center justify-between">
              <FieldHelpLabel
                helpKey={help.form.sendNotification}
                watchValue={field.value}
                showIcon={false}
              >
                <Label htmlFor="send_notification" className="text-foreground">
                  {t("form.send_notification")}
                </Label>
              </FieldHelpLabel>
              <FormControl>
                <Switch
                  id="send_notification"
                  name="send_notification"
                  disabled={isEditing}
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />
              </FormControl>
            </FormItem>
          )}
        />
        </fieldset>
        </SheetFormScrollBody>
        <SheetFooter className={FORM_SHEET_FOOTER_CLASS}>
          <Button
            type="submit"
            className="w-full"
            data-help-target={help.form.submit}
            disabled={isSubmitting || isHouseholdRestricted || submitBlockedByLocation || !hasAllRequiredFields}
          >
            {tAmenityAccessLogSubmitLabel(isEditing, t)}
          </Button>
        </SheetFooter>
      </form>
    </Form>
  );
}
